import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { getFileDetailsRequest } from '@/store/slices/fileUploadSlice';
import { Loader2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export default function FileDetails() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { fileDetails, isLoadingDetails, detailsError } = useAppSelector(
    (state) => state.fileUpload
  );

  useEffect(() => {
    if (id) {
      dispatch(getFileDetailsRequest(id));
    }
  }, [dispatch, id]);

  if (isLoadingDetails) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (detailsError) {
    return (
      <div className="p-4 text-red-500">
        Error loading file details: {detailsError}
      </div>
    );
  }

  if (!fileDetails) {
    return null;
  }

  // Function to determine status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'text-yellow-600';
      case 'FAILED':
      case 'FAILED_NO_PRODUCT_MATCH':
      case 'FAILED_MAX_NUMBER_OF_QUERY_REACHED':
      case 'FAILED_MAX_DASHBOARD_CAPACITY_REACHED':
        return 'text-red-600';
      case 'ALREADY_EXISTS':
        return 'text-purple-600';
      case 'SUCCEEDED':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-2xl font-semibold mb-6">File Upload Details</h1>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Given Title</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Matched Product</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {fileDetails.rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell>{row.row_data.title_en}</TableCell>
              <TableCell>
                <span className={`font-semibold ${getStatusColor(row.status)}`}>
                  {row.status}
                </span>
              </TableCell>
              <TableCell>
                <Link
                  to={`/product/${row.query_log?.product_group?.id}`}
                  className="text-blue-600 hover:underline"
                >
                  {row.query_log?.product_group?.title_en || 'N/A'}
                </Link>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
